// src/components/snowflakeCortexChat/AGGridTable.js
import React, { useMemo, useEffect, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import { ModuleRegistry, ClientSideRowModelModule } from "ag-grid-community";
import { LicenseManager } from "ag-grid-enterprise";
import { renderMarkdownAsHTML, stripCellForExport } from "../../lib/markdownRenderer";
import "./style.css";

// Register required modules (client-side rows)
ModuleRegistry.registerModules([ClientSideRowModelModule]);
LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-083969}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{TELUS_Agriculture_&_Consumer_Goods_(US,_PA,_6632)}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{TABSInsight}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{TABSInsight}_need_to_be_licensed___{TABSInsight}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{14_October_2026}____[v3]_[01]_MTc5MTkzMjQwMDAwMA==073ec8e13184eaa9da8a95a5efbcc8c5",
);
// Helper function for formatting numeric values
const createNumericFormatter = (format) => {
  return (params) => {
    if (params.value == null) return "";

    const num =
      typeof params.value === "number"
        ? params.value
        : parseFloat(params.value.toString().replace(/[$,%]/g, "").replace(/,/g, ""));

    if (isNaN(num)) return params.value;

    const formatted = num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    if (format === "currency") return `$${formatted}`;
    if (format === "percentage") return `${formatted}%`;
    return formatted;
  };
};

// Excel-only cell styles: keep numbers as real numeric cells (not text) so Excel's own
// number formatting is preserved. Currency pads decimals to 2 places ($3.10); plain
// numbers and percentages use a variable-decimal format (up to 2, no trailing zeros) to
// match the grid's display exactly. Percentages append a literal "%" suffix instead of
// Excel's percent type, which would otherwise multiply the stored value by 100.
const EXCEL_NUMBER_STYLES = [
  { id: "excel-currency-whole", numberFormat: { format: "$#,##0" } },
  { id: "excel-currency-decimal", numberFormat: { format: "$#,##0.00" } },
  { id: "excel-number-whole", numberFormat: { format: "#,##0" } },
  { id: "excel-number-decimal", numberFormat: { format: "#,##0.##" } },
  { id: "excel-percentage-whole", numberFormat: { format: "0\"%\"" } },
  { id: "excel-percentage-decimal", numberFormat: { format: "0.##\"%\"" } },
];

// Picks the excelStyle id matching a column's format + whether this particular value has decimals.
// Assumes numeric values are already "human" percentages (e.g. 45.2, not 0.452), matching
// createNumericFormatter's on-screen formatting below.
const getExcelNumberStyleId = (format, value) => {
  if (typeof value !== "number" || isNaN(value)) return null;
  const hasDecimals = Math.abs(value - Math.round(value)) > 1e-9;
  if (format === "currency") return hasDecimals ? "excel-currency-decimal" : "excel-currency-whole";
  if (format === "percentage") return hasDecimals ? "excel-percentage-decimal" : "excel-percentage-whole";
  return hasDecimals ? "excel-number-decimal" : "excel-number-whole";
};

const MIN_GRID_HEIGHT = 140;

const renderMarkdownCell = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (
    !trimmed.includes("**") &&
    !trimmed.includes("*") &&
    !trimmed.includes("`") &&
    !trimmed.includes("#") &&
    !trimmed.startsWith("- ")
  ) {
    return value;
  }

  return <span dangerouslySetInnerHTML={{ __html: renderMarkdownAsHTML(value) }} />;
};

const AGGridTable = ({ headers, rows, isNumericColumn, columnFormats, height, rowHeight = 36, headerHeight = 56 }) => {
  const gridRef = useRef(null);
  const gridHeight = Math.max(MIN_GRID_HEIGHT, Math.round(height || headerHeight + rows.length * rowHeight));

  const columnDefs = useMemo(() => {
    return headers.map((header, index) => {
      const colDef = {
        field: header,
        headerName: header,
        sortable: true,
        filter: true,
        resizable: true,
        cellClass: isNumericColumn[index]
          ? (params) => {
            const excelStyleId = getExcelNumberStyleId(columnFormats[index], params.value);
            return excelStyleId ? ["ag-right-aligned-cell", excelStyleId] : "ag-right-aligned-cell";
          }
          : "",
      };

      if (isNumericColumn[index]) {
        colDef.type = "numericColumn";
        colDef.chartDataType = "series";
        colDef.valueFormatter = createNumericFormatter(columnFormats[index]);
        // Parse the raw value to ensure it's numeric for charting
        colDef.valueParser = (params) => {
          if (params.newValue == null || params.newValue === "") return null;
          const parsed = parseFloat(params.newValue.toString().replace(/[$,%]/g, "").replace(/,/g, ""));
          return isNaN(parsed) ? null : parsed;
        };
      } else {
        colDef.chartDataType = "category";
        colDef.cellRenderer = (params) => renderMarkdownCell(params.value);
      }

      return colDef;
    });
  }, [headers, isNumericColumn, columnFormats]);

  const rowData = useMemo(() => {
    return rows.map((row) => {
      const rowObj = {};
      headers.forEach((header, index) => {
        let value = row[index];

        // If it's a numeric column, parse the value to a number
        if (isNumericColumn[index] && value != null) {
          const parsed =
            typeof value === "number"
              ? value
              : parseFloat(stripCellForExport(value.toString()).replace(/[$,%]/g, "").replace(/,/g, ""));
          rowObj[header] = isNaN(parsed) ? value : parsed;
        } else {
          rowObj[header] = value;
        }
      });
      return rowObj;
    });
  }, [rows, headers, isNumericColumn]);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      filter: true,
      sortable: true,
      resizable: true,
    }),
    [],
  );

  // Estimates a per-column Excel width (px) from the header and formatted cell content, so
  // exported columns auto-fit their values instead of using the grid's on-screen widths.
  // Computed lazily (only when exporting) rather than as a useMemo, since it's O(rows x cols)
  // and irrelevant to every other rowData/columnFormats change.
  const computeExcelColumnWidths = useCallback(() => {
    const widths = {};
    headers.forEach((header, index) => {
      const formatter = isNumericColumn[index] ? createNumericFormatter(columnFormats[index]) : null;
      let maxLen = header.length;
      rowData.forEach((row) => {
        const value = row[header];
        if (value == null) return;
        const text = formatter ? formatter({ value }) : String(value);
        maxLen = Math.max(maxLen, String(text).length);
      });
      widths[header] = Math.min(400, Math.max(70, maxLen * 7 + 24));
    });
    return widths;
  }, [headers, rowData, isNumericColumn, columnFormats]);

  const gridOptions = useMemo(
    () => ({
      animateRows: true,
      cellSelection: true,
      clipboardDelimiter: "\t",
      rowSelection: {
        mode: "multiRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: false,
        copySelectedRows: false,
      },
      domLayout: "normal", // Use normal layout for proper scrolling with fixed headers
      rowHeight,
      headerHeight,
    }),
    [rows.length, rowHeight, headerHeight],
  );

  // Cleanup on unmount
  useEffect(() => {
    const api = gridRef.current?.api;
    return () => {
      api?.destroy();
    };
  }, []);

  // Replace the default combined "Export" item so CSV exports raw numbers (no $/%/,
  // formatting) while Excel keeps the formatted, real-numeric-type output from excelStyles.
  const getContextMenuItems = useCallback(
    (params) => {
      const otherItems = (params.defaultItems || []).filter((item) => item !== "export");
      return [
        ...otherItems,
        "separator",
        {
          name: "Export to CSV",
          action: () => {
            // processCellCallback returns the raw parsed value (not the formatted display
            // string) to intentionally bypass each column's valueFormatter for CSV output.
            params.api.exportDataAsCsv({
              processCellCallback: (cellParams) => (cellParams.value == null ? "" : stripCellForExport(cellParams.value)),
              processHeaderCallback: (headerParams) => stripCellForExport(headerParams.column.getColDef().headerName || ""),
            });
          },
        },
        {
          name: "Export to Excel (.xlsx)",
          action: () => {
            const excelColumnWidths = computeExcelColumnWidths();
            params.api.exportDataAsExcel({
              processCellCallback: (cellParams) => {
                const cleaned = stripCellForExport(cellParams.value);
                const isNumericCol = cellParams.column.getColDef().type === "numericColumn";
                return isNumericCol && typeof cleaned === "string" ? cleaned.replace(/^\+\s*/, "") : cleaned;
              },
              processHeaderCallback: (headerParams) => stripCellForExport(headerParams.column.getColDef().headerName || ""),
              columnWidth: (colParams) => excelColumnWidths[colParams.column.getColId()] || 100,
            });
          },
        },
      ];
    },
    [computeExcelColumnWidths],
  );

  return (
    <div className="rgm-chat-table-container" style={{ width: "100%" }} data-testid="snowflake-chat-grid">
      <div style={{ marginBottom: "8px", fontSize: "12px", color: "#666" }}>
        {rows.length} rows - Right-click for options - Select cells to copy
      </div>
      <div
        className="ag-theme-alpine rgm-chat-table-grid"
        style={{ height: gridHeight, maxHeight: gridHeight, minHeight: MIN_GRID_HEIGHT }}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          excelStyles={EXCEL_NUMBER_STYLES}
          getContextMenuItems={getContextMenuItems}
          enableCellTextSelection={false}
          ensureDomOrder={true}
          suppressCellFocus={false}
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  );
};

export default AGGridTable;
